<template>
  <div class="flex flex-col gap-4">
    <!--
      7b — nothing logged yet, and the user has not started: one empty state
      instead of two, where the legacy wizard drew this once under
      Contributions and once under Distributions.
    -->
    <div
      v-if="!showTable"
      class="flex flex-col items-center gap-1.5 px-6 py-8 text-center border border-dashed border-border rounded-ds-lg"
    >
      <ArrowDownUp class="size-5 mb-1 text-muted-foreground" />
      <p class="text-sm font-bold text-foreground">
        No cash flows yet
      </p>
      <p class="max-w-md text-xs font-medium leading-normal text-muted-foreground">
        Skip this and the fund shows a {{ commitmentLabel }} commitment with nothing called. Capital
        calls and distributions can be logged from the asset page whenever notices arrive.
      </p>
      <DSButton
        type="button"
        size="sm"
        class="mt-2.5"
        @click="hasStartedAdding = true"
      >
        <Plus class="size-4" />
        Add a cash flow
      </DSButton>
    </div>

    <!-- 7a — the merged table: one row per flow, direction read off its own type. -->
    <template v-else>
      <div class="grid grid-cols-1 xs:grid-cols-3 gap-2.5">
        <div class="rounded-ds-md border border-border p-3">
          <div class="text-[10.5px] font-bold uppercase tracking-ds-caps text-muted-foreground">
            Called to date
          </div>
          <div class="mt-0.5 text-lg font-bold tracking-ds-tight text-foreground">
            {{ money(called) }}
          </div>
          <div class="mt-0.5 text-xs font-medium text-muted-foreground">
            {{ calledPercent === undefined ? '—' : `${calledPercent}% of commitment` }}
          </div>
        </div>
        <div class="rounded-ds-md border border-border p-3">
          <div class="text-[10.5px] font-bold uppercase tracking-ds-caps text-muted-foreground">
            Distributed
          </div>
          <div class="mt-0.5 text-lg font-bold tracking-ds-tight text-foreground">
            {{ money(distributed) }}
          </div>
          <div class="mt-0.5 text-xs font-medium text-muted-foreground">
            {{ distCount }} {{ distCount === 1 ? 'distribution' : 'distributions' }}
          </div>
        </div>
        <div class="rounded-ds-md border border-border p-3">
          <div class="text-[10.5px] font-bold uppercase tracking-ds-caps text-muted-foreground">
            Net contributed
          </div>
          <div class="mt-0.5 text-lg font-bold tracking-ds-tight text-foreground">
            {{ money(net) }}
          </div>
          <div class="mt-0.5 text-xs font-medium text-muted-foreground">
            {{ unfundedAmount === undefined ? '—' : `Unfunded ${money(unfundedAmount)}` }}
          </div>
        </div>
      </div>

      <div class="overflow-hidden rounded-ds-lg border border-border">
        <DSTable class="block sm:table table-fixed">
          <colgroup>
            <col class="w-[20%]">
            <col class="w-[22%]">
            <col class="w-[20%]">
            <col class="w-[18%]">
            <col class="w-[7%]">
          </colgroup>
          <DSTableHeader class="hidden sm:table-header-group">
            <DSTableRow class="bg-background">
              <DSTableHead>Investor</DSTableHead>
              <DSTableHead>Date</DSTableHead>
              <DSTableHead>Type</DSTableHead>
              <DSTableHead class="text-right">
                Amount
              </DSTableHead>
              <DSTableHead />
            </DSTableRow>
          </DSTableHeader>

          <DSTableBody class="block sm:table-row-group">
            <DSTableRow
              v-for="row in rows"
              :key="row.index"
              class="flex flex-col sm:table-row max-sm:relative max-sm:py-2"
            >
              <DSTableCell class="py-1 max-sm:px-4 min-w-0 text-sm font-bold text-foreground">
                <span class="inline-flex min-w-0 items-center gap-1.5 w-full">
                  <Sparkles
                    v-if="cashFlowMark(row.index)"
                    class="size-3 shrink-0 text-brand"
                  />
                  <span class="min-w-0 truncate">{{ investorLabel(row.flow.investorIndex) }}</span>
                </span>
              </DSTableCell>
              <DSTableCell class="py-1 max-sm:px-4 text-sm font-medium text-foreground">
                {{ dateLabel(row.flow.date) }}
              </DSTableCell>
              <DSTableCell class="py-1 max-sm:px-4 ">
                <span class="inline-flex min-w-0 items-center gap-1.5">
                  <ArrowDownLeft
                    v-if="isContribution(row.flow.type)"
                    class="size-3.5 shrink-0 text-muted-foreground"
                  />
                  <ArrowUpRight
                    v-else
                    class="size-3.5 shrink-0 text-success"
                  />
                  <span class="truncate text-sm font-medium text-foreground">
                    {{ CASH_FLOW_TYPE_LABEL[row.flow.type] ?? row.flow.type }}
                  </span>
                </span>
              </DSTableCell>
              <DSTableCell
                class="py-1 max-sm:px-4 sm:text-right text-sm font-bold"
                :class="isDistribution(row.flow.type) ? 'text-success' : 'text-foreground'"
              >
                {{ isContribution(row.flow.type) ? '−' : '+' }}{{ money(row.flow.amount) }}
              </DSTableCell>
              <DSTableCell class="py-1 max-sm:px-4 sm:text-center max-sm:absolute max-sm:right-0 max-sm:top-1">
                <DSButton
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  class="shrink-0 text-muted-foreground hover:bg-destructive-subtle hover:text-destructive-subtle-foreground"
                  :aria-label="`Remove ${CASH_FLOW_TYPE_LABEL[row.flow.type] ?? row.flow.type} on ${dateLabel(row.flow.date)}`"
                  @click="handleRemove(row.index)"
                >
                  <Trash2 />
                </DSButton>
              </DSTableCell>
            </DSTableRow>

            <DSTableRow class="flex flex-col sm:table-row align-top bg-accent!">
              <DSTableCell class="min-w-0 py-2.5 max-sm:px-4 overflow-hidden">
                <DSSelect
                  :model-value="draft.investorIndex === undefined ? undefined : String(draft.investorIndex)"
                  @update:model-value="value => draft.investorIndex = value === undefined ? undefined : Number(value)"
                >
                  <DSSelectTrigger>
                    <DSSelectValue placeholder="Choose investor" />
                  </DSSelectTrigger>
                  <DSSelectContent>
                    <DSSelectItem
                      v-for="(investor, index) in fundInvestors"
                      :key="index"
                      :value="String(index)"
                    >
                      {{ investor.accountName || `Investor ${index + 1}` }}
                    </DSSelectItem>
                  </DSSelectContent>
                </DSSelect>
              </DSTableCell>
              <DSTableCell class="min-w-0 py-2.5 max-sm:px-4 overflow-hidden">
                <DSDateField
                  v-model="draft.date"
                  :aria-label="'Date'"
                />
              </DSTableCell>
              <DSTableCell class="min-w-0 py-2.5 max-sm:px-4 overflow-hidden">
                <DSSelect v-model="draft.type">
                  <DSSelectTrigger>
                    <DSSelectValue placeholder="Choose type" />
                  </DSSelectTrigger>
                  <DSSelectContent>
                    <DSSelectGroup class="-mt-2">
                      <DSSelectLabel class="flex items-center gap-2.5 bg-background pl-3 py-2.5 pr-0 -mx-2 my-1">
                        <ArrowDownLeft class="size-4" />
                        Money in to the fund
                      </DSSelectLabel>
                      <DSSelectItem
                        v-for="type in CONTRIBUTION_TYPES"
                        :key="type"
                        :value="type"
                      >
                        {{ CASH_FLOW_TYPE_LABEL[type] }}
                      </DSSelectItem>
                    </DSSelectGroup>
                    <DSSelectGroup>
                      <DSSelectLabel class="flex items-center gap-2.5 bg-background pl-3 py-2.5 pr-0 -mx-2 my-1">
                        <ArrowUpRight class="size-4 text-success" />
                        Money out to you
                      </DSSelectLabel>
                      <DSSelectItem
                        v-for="type in DISTRIBUTION_TYPES"
                        :key="type"
                        :value="type"
                      >
                        {{ CASH_FLOW_TYPE_LABEL[type] }}
                      </DSSelectItem>
                    </DSSelectGroup>
                  </DSSelectContent>
                </DSSelect>
              </DSTableCell>
              <DSTableCell class="py-2.5 max-sm:px-4">
                <DSAmountField
                  :key="amountFieldKey"
                  :model-value="draft.amount"
                  placeholder="0.00"
                  @update:model-value="value => draft.amount = value"
                />
              </DSTableCell>
              <DSTableCell class="text-center py-2.5 max-sm:px-4">
                <DSButton
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  :disabled="!canAdd"
                  class="sm:flex items-center gap-2"
                  aria-label="Add this cash flow"
                  @click="handleAdd"
                >
                  <Plus class="size-4 text-brand shrink-0" />
                  <span class="sm:hidden">Add this cash flow</span>
                </DSButton>
              </DSTableCell>
            </DSTableRow>
          </DSTableBody>
        </DSTable>
      </div>

      <div class="flex items-start gap-2 text-xs font-medium text-muted-foreground">
        <Info class="mt-0.5 size-3.5 shrink-0" />
        <span>Direction comes from the type, so amounts sign themselves and rows sort by date the way a
          capital account statement reads.</span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { ArrowDownLeft, ArrowDownUp, ArrowUpRight, Info, Plus, Sparkles, Trash2 } from '@lucide/vue'

import {
  DSAmountField,
  DSButton,
  DSDateField,
  DSSelect,
  DSSelectContent,
  DSSelectGroup,
  DSSelectItem,
  DSSelectLabel,
  DSSelectTrigger,
  DSSelectValue,
  DSTable,
  DSTableBody,
  DSTableCell,
  DSTableHead,
  DSTableHeader,
  DSTableRow,
} from '@ds'

import {
  AssetWizardAccount,
  AssetWizardCashFlow,
  AssetWizardEntity,
  AssetWizardInvestor,
} from '@/store/ai/assetWizard/utils/types'

import { getCurrentDate, stringToLocalDateString } from '@/helpers/dates'
import { getCurrencySymbol } from '@/helpers/common'
import { numberFormat } from '@/helpers/numbers'
import { FRACTION_DIGITS } from '@/const'

import {
  calledToDate,
  distributedToDate,
  distributionCount,
  isContribution,
  isDistribution,
  netContributed,
  unfunded,
} from '../utils/helpers'
import { ACCOUNT_MODE_FIELD, CASH_FLOW_TYPE_LABEL, CONTRIBUTION_TYPES, DISTRIBUTION_TYPES } from '../utils/const'
import { WizardAccountMode, WizardCashFlow, WizardFieldDef, WizardFieldOrigin, WizardInvestor } from '../utils/types'

/*
 * The step the generic field grid cannot express any more than the
 * investing-entities step can: a merged log of capital calls, fees and
 * distributions — one table rather than the legacy wizard's two, since which
 * types a row may take is exactly what the dropdown groups by (see
 * CONTRIBUTION_TYPES / DISTRIBUTION_TYPES), not a decision the step should
 * ask twice for.
 *
 * Shared by both of a fund's own wizards — private_fund_subscribe_and_commit
 * and private_fund alike carry a real `values.investors` list to log against
 * (seeded by EntitiesStep.vue for the latter, share-less), so the table above
 * needs nothing else changed to serve both. `fundInvestors`' own
 * `ownerAccountName` fallback below is unused by either flow today and kept
 * only in case a future flow logs cash flows against a single `ownerAccount`
 * instead of a list.
 *
 * Optional throughout: a fund with nothing called yet is still a valid one
 * to create, and every stat and row here is read back, never required — see
 * `wizardSteps`'s own CASH_FLOWS_STEP_KEY branch.
 */
const { form, cashFlows: suggested } = defineProps<{
  form: any
  fields: WizardFieldDef[]
  origins: Record<string, WizardFieldOrigin>
  /** Declared so Wizard.vue can hand every bespoke step the same bag. Unused here. */
  transactionType?: string
  flow?: string
  ownerAccount?: AssetWizardAccount
  investors?: AssetWizardInvestor[]
  entities?: AssetWizardEntity[]
  /**
   * What the read suggested, if it suggested anything — see `seedCashFlows`.
   * The rows themselves are already in the form; this is only for
   * `cashFlowMark`, which needs the count to know how many of them are its.
   */
  cashFlows?: AssetWizardCashFlow[]
  sentence?: string
}>()

/*
 * private_fund_subscribe_and_commit's own split, when the form carries one —
 * otherwise (private_fund, a single position) the one row the ownership step
 * collected instead, read off the same three fields `ensureOwnerAccount`
 * resolves. Either way this is a `WizardInvestor[]`, so the table and the
 * draft row below need not know which flow they are in: an "Investor" picker
 * with one option is still the same control, correctly labelled.
 */
const investorsSelector = form.useSelector((state: any): WizardInvestor[] => {
  if (state.values.investors?.length) return state.values.investors as WizardInvestor[]
  if (!state.values.ownerAccountName) return []

  return [{
    accountMode: state.values[ACCOUNT_MODE_FIELD] ?? WizardAccountMode.EXISTING,
    accountId: state.values.ownerAccountId ?? '',
    accountName: state.values.ownerAccountName,
    accountType: state.values.ownerAccountType ?? '',
    share: undefined,
  }]
})
const commitmentSelector = form.useSelector((state: any) => state.values.amount)
const currencySelector = form.useSelector((state: any) => state.values.currency)
const cashFlowsSelector = form.useSelector((state: any): WizardCashFlow[] => state.values.cashFlows ?? [])

const fundInvestors = computed<WizardInvestor[]>(() => investorsSelector.value)
const commitment = computed<number | undefined>(() => commitmentSelector.value)
const currency = computed<string | undefined>(() => currencySelector.value)
const cashFlows = computed<WizardCashFlow[]>(() => cashFlowsSelector.value)

const setCashFlows = (next: WizardCashFlow[]) => form.setFieldValue('cashFlows', next)

/*
 * Set once the user has actually asked to start, so a fund with nothing
 * logged still opens on 7b rather than an empty table nobody asked for.
 * Stays true for the rest of this visit even if every row is then removed —
 * reverting to the empty state mid-edit would read as the wizard undoing a
 * choice the user did not make.
 */
const hasStartedAdding = ref(false)
const showTable = computed(() => cashFlows.value.length > 0 || hasStartedAdding.value)

const commitmentLabel = computed(() => (commitment.value === undefined ? 'the' : money(commitment.value)))

/*
 * Paired with the row's own position in `cashFlows` — not sorted in place —
 * so removing a row always splices the entry actually shown, whatever order
 * the table draws it in. See `sortedCashFlows` for the order-only version.
 */
const rows = computed(() =>
  cashFlows.value
    .map((flow, index) => ({ flow, index }))
    .sort((a, b) => a.flow.date.localeCompare(b.flow.date)))

const investorLabel = (index: number) => fundInvestors.value[index]?.accountName || `Investor ${index + 1}`

const dateLabel = (date: string) => stringToLocalDateString(date) ?? date

/*
 * Sparkle the rows `seedCashFlows` actually filled — addressed by each row's
 * own position in `cashFlows` (`row.index`, from the unsorted, insertion-
 * ordered array), not by where the table happens to draw it: `rows` sorts by
 * date for display, so "the first N" only means the read's own rows if it is
 * read off the position they were seeded at rather than off display order.
 * A row added afterward with "Add this cash flow" carries no mark; same rule
 * `investorMark` already follows in InvestorsStep.vue.
 */
const cashFlowMark = (index: number) => index < (suggested?.length ?? 0)

const called = computed(() => calledToDate(cashFlows.value))
const distributed = computed(() => distributedToDate(cashFlows.value))
const net = computed(() => netContributed(cashFlows.value))
const distCount = computed(() => distributionCount(cashFlows.value))
const unfundedAmount = computed(() => unfunded(cashFlows.value, commitment.value))
const calledPercent = computed(() =>
  commitment.value ? Math.round((called.value / commitment.value) * 1000) / 10 : undefined)

const money = (amount?: number) =>
  amount === undefined
    ? '—'
    : numberFormat(amount, {
      currency: getCurrencySymbol(currency.value),
      fractionDigits: 0,
      fractionDigitsMax: FRACTION_DIGITS,
    })

/*
 * The row being typed, held here rather than in the form: it is not
 * submitted until "Add" is pressed, the same reason ValuationStep keeps its
 * own draft mark outside the form's values.
 */
const draft = reactive<{
  investorIndex?: number
  date: string
  type: string
  amount?: number
}>({
  investorIndex: undefined,
  date: getCurrentDate({ iso: true }),
  type: '',
  amount: undefined,
})

const canAdd = computed(() =>
  draft.investorIndex !== undefined &&
  !!draft.date &&
  !!draft.type &&
  typeof draft.amount === 'number' &&
  draft.amount > 0)

/*
 * Forces the amount field to remount after every add. Reka's NumberField
 * does not reliably pick up an external reset of its own `model-value` back
 * to `undefined` — the field keeps whatever it last had internally, and the
 * next thing typed lands on top of that stale buffer instead of starting
 * fresh, so nothing commits back to `draft.amount` until a second attempt.
 * A fresh `:key` sidesteps it by giving the row a brand new instance instead
 * of asking the old one to forget what it was showing.
 */
const amountFieldKey = ref(0)

const handleAdd = () => {
  if (!canAdd.value) return

  setCashFlows([
    ...cashFlows.value,
    { investorIndex: draft.investorIndex!, date: draft.date, type: draft.type, amount: draft.amount! },
  ])

  // Investor, date and type are sticky — a statement is usually several
  // rows for the same entity on the same day. Only the amount, which is
  // never the same twice, is cleared.
  draft.amount = undefined
  amountFieldKey.value++
}

const handleRemove = (index: number) => {
  setCashFlows(cashFlows.value.filter((_, i) => i !== index))
}
</script>
